import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { SERVER_TIME_TOKEN, ServerTime } from './server-time.interface';

interface TimeSyncResult {
  serverTime: Date;
  clientTime: Date;
  skew: number; // Time skew in milliseconds
  averageSkew: number; // Rolling average of time skew
}

@Injectable({
  providedIn: 'root'
})
export class ServerTimeSyncService {
  private serverTimeService = inject<ServerTime>(SERVER_TIME_TOKEN);

  private skewMeasurements: number[] = [];
  private maxMeasurements = 5;

  /**
   * Measures the time skew between the client and the server.
   * @returns An Observable that emits a TimeSyncResult object.
   */
  public getTimeSkew(): Observable<TimeSyncResult> {
    const clientTimeBeforeRequest = Date.now();

    return this.serverTimeService.fetchServerTime().pipe(
      map((serverTimeMilliseconds) => {
        const clientTimeAfterResponse = Date.now();

        // Calculate round-trip time (RTT) and one-way delay
        const rtt = clientTimeAfterResponse - clientTimeBeforeRequest;
        const oneWayDelay = rtt / 2;

        // Calculate time skew using Cristian's Algorithm
        const skew = serverTimeMilliseconds + oneWayDelay - clientTimeAfterResponse;

        // Update the rolling average of skew measurements
        this.updateSkewMeasurements(skew);
        const averageSkew = this.calculateAverageSkew();

        return {
          serverTime: new Date(serverTimeMilliseconds),
          clientTime: new Date(clientTimeBeforeRequest),
          skew: skew,
          averageSkew: averageSkew
        };
      })
    );
  }

  /**
   * Updates the skew measurements array with the latest skew value.
   * Maintains a maximum of `maxMeasurements` entries.
   * @param skew The latest skew measurement in milliseconds.
   */
  private updateSkewMeasurements(skew: number): void {
    if (this.skewMeasurements.length >= this.maxMeasurements) {
      this.skewMeasurements.shift(); // Remove the oldest measurement
    }
    this.skewMeasurements.push(skew);
  }

  /**
   * Calculates the average skew from the stored measurements.
   * @returns The average skew in milliseconds.
   */
  private calculateAverageSkew(): number {
    const sum = this.skewMeasurements.reduce((acc, curr) => acc + curr, 0);
    return sum / this.skewMeasurements.length;
  }
}
